"use server";

import { apiFetchServer } from "@/src/lib/api/fetch.server";
import { expressHttp } from "@/src/lib/api/http";
import { actorFromJwt, decodeJwtPayload } from "@/src/lib/api/jwt";
import { parseApiResponse } from "@/src/lib/api/parse-response";
import {
  clearTokenCookies,
  getAccessTokenServer,
  getRefreshTokenServer,
  setTokenCookies,
} from "@/src/lib/api/tokens.server";
import {
  ApiError,
  type AccountType,
  type Actor,
  type AcceptInviteInput,
  type LoginInput,
  type MeResponse,
  type SignupInput,
  type TokenPair,
} from "@/src/lib/api/types";

function actorFromMe(me: MeResponse): Actor {
  const roleCodes = me.roles.map((r) => r.code);
  const isAdmin = roleCodes.length > 0;

  let type: AccountType = "customer";
  if (roleCodes.includes("owner")) type = "owner";
  else if (
    roleCodes.includes("shop_admin") ||
    roleCodes.includes("super_admin")
  ) {
    type = "shop_admin";
  } else if (isAdmin) {
    type = "staff";
  }

  return {
    id: me.id,
    type,
    roles: roleCodes,
    isAdmin,
    email: me.email,
    name: me.name,
    active: me.active,
  };
}

async function persistTokenPair(pair: TokenPair): Promise<Actor> {
  await setTokenCookies(pair.accessToken, pair.refreshToken);

  const payload = decodeJwtPayload(pair.accessToken);
  if (payload) return actorFromJwt(payload);

  const me = await apiFetchServer<MeResponse>("/users/me");
  return actorFromMe(me);
}

/** migrated from supabase: signInWithPassword */
export async function login(input: LoginInput): Promise<Actor> {
  const res = await expressHttp.post("/auth/login", input, {
    headers: { "Content-Type": "application/json", "Cache-Control": "no-store" },
  });

  const pair = await parseApiResponse<TokenPair>(res);
  return persistTokenPair(pair);
}

/** migrated from supabase: exchangeCodeForSession + updateUser (invite accept) */
export async function acceptInvite(input: AcceptInviteInput): Promise<Actor> {
  const res = await expressHttp.post("/auth/accept-invite", input, {
    headers: { "Content-Type": "application/json", "Cache-Control": "no-store" },
  });

  const pair = await parseApiResponse<TokenPair>(res);
  return persistTokenPair(pair);
}

/** migrated from supabase: signUp (+ separate customer create — now single-step) */
export async function signup(input: SignupInput): Promise<Actor> {
  const res = await expressHttp.post("/auth/signup", input, {
    headers: { "Content-Type": "application/json", "Cache-Control": "no-store" },
  });

  const pair = await parseApiResponse<TokenPair>(res);
  return persistTokenPair(pair);
}

/** migrated from supabase: signOut */
export async function logout(): Promise<void> {
  const refreshToken = await getRefreshTokenServer();
  if (refreshToken) {
    try {
      await expressHttp.post(
        "/auth/logout",
        { refreshToken },
        {
          headers: {
            "Content-Type": "application/json",
            "Cache-Control": "no-store",
          },
        },
      );
    } catch {
      // ! Best-effort revoke — always clear local cookies
    }
  }
  await clearTokenCookies();
}

/** migrated from supabase: session refresh (opaque refresh token rotation) */
export async function refreshSession(): Promise<string | null> {
  const refreshToken = await getRefreshTokenServer();
  if (!refreshToken) return null;

  let res;
  try {
    res = await expressHttp.post(
      "/auth/refresh",
      { refreshToken },
      {
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-store",
        },
      },
    );
  } catch {
    return null;
  }

  if (res.status < 200 || res.status >= 300) {
    await clearTokenCookies();
    return null;
  }

  try {
    const pair = await parseApiResponse<TokenPair>(res);
    await setTokenCookies(pair.accessToken, pair.refreshToken);
    return pair.accessToken;
  } catch {
    await clearTokenCookies();
    return null;
  }
}

/** Request a password-reset email (dev: token echoed in API response). */
export async function forgotPassword(
  email: string,
): Promise<{ ok: true }> {
  const res = await expressHttp.post(
    "/auth/forgot-password",
    { email },
    {
      headers: {
        "Content-Type": "application/json",
        "Cache-Control": "no-store",
      },
    },
  );
  await parseApiResponse(res);
  return { ok: true };
}

/** Complete password reset with token from email link. */
export async function resetPassword(input: {
  token: string;
  password: string;
}): Promise<void> {
  const res = await expressHttp.post("/auth/reset-password", input, {
    headers: { "Content-Type": "application/json", "Cache-Control": "no-store" },
  });
  if (res.status < 200 || res.status >= 300) {
    const body = res.data as { error?: string };
    throw new Error(body?.error ?? "Password reset failed");
  }
}

/** Complete email verification with token from email link. */
export async function verifyEmail(token: string): Promise<void> {
  const res = await expressHttp.post("/auth/verify-email", { token }, {
    headers: { "Content-Type": "application/json", "Cache-Control": "no-store" },
  });
  if (res.status < 200 || res.status >= 300) {
    const body = res.data as { error?: string };
    throw new Error(body?.error ?? "Email verification failed");
  }
}

/** Change password for the signed-in user. */
export async function changePassword(input: {
  currentPassword: string;
  newPassword: string;
}): Promise<{ success: boolean; error?: string }> {
  try {
    await apiFetchServer<void>("/auth/change-password", {
      method: "POST",
      body: JSON.stringify(input),
    });
    return { success: true };
  } catch (e) {
    if (e instanceof ApiError) {
      return { success: false, error: e.message };
    }
    return { success: false, error: "Failed to update password" };
  }
}

/** migrated from supabase: re-auth before password change */
export async function verifyPassword(
  password: string,
): Promise<{ success: boolean; error?: string }> {
  try {
    await apiFetchServer<void>("/auth/verify-password", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
    return { success: true };
  } catch (e) {
    if (e instanceof ApiError) {
      return { success: false, error: e.message };
    }
    return { success: false, error: "Verification failed" };
  }
}

/** migrated from supabase: auth.getUser() — server-side actor resolution */
/** Full session profile for client layouts (email, name, roles). */
export async function getSessionUser(): Promise<Actor | null> {
  const accessToken = await getAccessToken();
  if (!accessToken) return null;
  try {
    const me = await apiFetchServer<MeResponse>("/users/me");
    return actorFromMe(me);
  } catch {
    const payload = decodeJwtPayload(accessToken);
    return payload ? actorFromJwt(payload) : null;
  }
}

export async function getActor(): Promise<Actor | null> {
  const accessToken = await getAccessTokenServer();
  if (accessToken) {
    const payload = decodeJwtPayload(accessToken);
    if (payload) return actorFromJwt(payload);
  }

  const refreshToken = await getRefreshTokenServer();
  if (!refreshToken) return null;

  const newAccess = await refreshSession();
  if (!newAccess) return null;

  const payload = decodeJwtPayload(newAccess);
  return payload ? actorFromJwt(payload) : null;
}

/** migrated from supabase: session access token for Bearer attachment */
export async function getAccessToken(): Promise<string | null> {
  const accessToken = await getAccessTokenServer();
  if (accessToken && decodeJwtPayload(accessToken)) {
    return accessToken;
  }
  return refreshSession();
}
